import moment from 'moment-timezone'
import { parse } from 'papaparse'
import { RolesAppMapping } from 'shared/constants'

export const showLimitedItems = (retailer, role) =>
  !(
    retailer?.settings?.enable_order_create === false &&
    role === RolesAppMapping.PARTNER_LIMITED.role
  )

export function capitalize(inputString) {
  return (
    inputString.charAt(0).toUpperCase() + inputString.slice(1).toLowerCase()
  )
}

/**
 * Pluralizes a string.
 * @param {number} count The number that determines if the string is plural.
 * @param {string} noun The string to make plural.
 * @param {string} suffix The string suffix.
 * @return {string} The pluralized string.
 */
export const pluralizeString = (count, noun, suffix = 's') =>
  `${count} ${noun}${count !== 1 ? suffix : ''}`

/**
 * Shortens a string to a specified length.
 * @param {string} str The string to shorten.
 * @param {number} charLength The total character length. Defaults to 9.
 * @return {string} The shortened string value.
 */
export const shortenString = (str, charLength = 9) => str.slice(0, charLength)

export const getParameterByName = (name, url = window.location.search) => {
  const normalized = name.replace(/[[\]]/g, '\\$&') // Remove slashes

  // Find where string is present
  const regex = new RegExp(`[?&]${normalized}(=([^&#]*)|&|#|$)`)
  const results = regex.exec(url)

  if (!results) return null
  if (!results[2]) return ''

  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

/**
 * Shortens a string to a specified starting and ending character count.
 * Fills in between characters with ellipses.
 * @param {str} str The string to shorten.
 * @param {number} startingCount The first starting character count to display.
 * @param {number} endingCount The last character count to display.
 */

export const shortenTextWithEllipses = (
  str,
  startingCount = 5,
  endingCount = 8
) => {
  if (startingCount + endingCount > str.length) {
    return str
  }

  const firstChars = str.substring(0, startingCount)
  const lastChars = str.substring(str.length - endingCount, str.length)

  return `${firstChars}...${lastChars}`
}

export const getCookie = (name) => {
  const value = `; ${document.cookie}`
  const split = value.split(`; ${name}=`)

  if (split.length === 2) {
    return split.pop().split(';').shift()
  }
}

export const removeCookie = (name) => {
  document.cookie = `${name}= ; expires=Thu, 01 Jan 1970 00:00:00 UTC`
}

export const setUuidLinks = (links = [], uuid = '/') => {
  const updatedLinks = links.map((link) => ({
    ...link,
    href: link.href.replace(':uuid', uuid),
  }))

  return updatedLinks
}

export const formatNumber = (num = '') =>
  num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')

export const copyToClipboard = (target) => {
  if (target instanceof Element || target instanceof HTMLDocument) {
    target.select()
    return document.execCommand('copy')
  }

  return false
}

export const getMimeType = (fileExtension) => {
  switch (fileExtension) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    case 'pdf':
      return 'application/pdf'
    case 'heic':
      return 'image/heic'
    case 'tiff':
      return 'image/tiff'
    case 'mp4':
      return 'video/mp4'
    case 'mov':
      return 'video/quicktime'
    case 'wmv':
      return 'video/x-ms-wmv'
    default:
      return 'image/png'
  }
}

export const downloadFile = (file, filename = '') => {
  // only allow download for File or Blob type objects.
  if (file instanceof Blob || file instanceof File) {
    const url = window.URL.createObjectURL(file)

    const a = document.createElement('a')
    a.style.display = 'none'
    a.href = url
    a.setAttribute('download', filename)
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)

    return
  }

  // eslint-disable-next-line no-console
  console.error('Expected file type to be File or Blob')
  return null
}

export const base64ToArrayBuffer = (base64) => {
  const binaryString = window.atob(base64)
  const binaryLen = binaryString.length
  const bytes = new Uint8Array(binaryLen)

  for (let i = 0; i < binaryLen; i++) {
    const ascii = binaryString.charCodeAt(i)
    bytes[i] = ascii
  }

  return bytes
}

export const convertTimeToEST = (inputTime) => {
  // Returns date without time
  return moment(inputTime).tz('America/New_York').format('l')
}

export const calculateExpire = (created_date, duration_months, format = 'l') =>
  moment(created_date).add(duration_months, 'months').format(format)

// Sanitize form values
export const sanitizeValues = (formValues) =>
  Object.keys(formValues).reduce((acc, curr) => {
    acc[curr] =
      typeof formValues[curr] === 'string'
        ? formValues[curr].trim()
        : formValues[curr]

    return acc
  }, {})

export const configureZendeskScript = () => {
  // Start of mulberrytechnology Zendesk Widget script
  const script = document.createElement('script')
  script.src =
    'https://static.zdassets.com/ekr/snippet.js?key=b22ce06c-23cc-493b-8d7c-d8bd47b43f28'
  script.id = 'ze-snippet'

  return document.getElementsByTagName('head')[0].appendChild(script)
}

export const identifyZendeskUser = (values) => {
  try {
    window.zE && window.zE('webWidget', 'identify', { ...values })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('zendesk error: ', err)
  }
}

export const prefillZendeskOptions = (values) => {
  try {
    window.zE && window.zE('webWidget', 'prefill', { ...values })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('zendesk error: ', err)
  }
}

// Promise-based timeout
export const sleep = async (dur) => {
  return new Promise((resolve) => setTimeout(resolve, dur))
}

// Eases between two values over a specified duration.
export class Ease {
  constructor(props) {
    this.set(props)
  }

  set(props) {
    Object.assign(
      this,
      {
        callback: () => {},
        duration: 500,
        easing: 'easeInOutCubic',
        initialValue: 0,
        onStart: () => {},
        onEnd: () => {},
        targetValue: 1,
      },
      props
    )
    this.delta = this.targetValue - this.initialValue
    this.timingFunc =
      this.timingFunc || this.timing[this.easing] || this.timing.easeInOutCubic
  }

  timing = {
    linear: (x) => x,
    easeInOutCubic: function (x) {
      return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2
    },
    easeInOutQuart: function (x) {
      return x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2
    },
    easeInOutQuint: function (x) {
      return x < 0.5 ? 16 * x * x * x * x * x : 1 - Math.pow(-2 * x + 2, 5) / 2
    },
    easeInOutExpo: function (x) {
      return x === 0
        ? 0
        : x === 1
        ? 1
        : x < 0.5
        ? Math.pow(2, 20 * x - 10) / 2
        : (2 - Math.pow(2, -20 * x + 10)) / 2
    },
  }

  animate = (t) => {
    if (t - this.startTime >= this.duration) {
      this.callback(this.targetValue)
      this.onEnd(this.targetValue)
    } else {
      const currentTime = t - this.startTime
      const progress = currentTime / this.duration
      const currentValue =
        this.initialValue + this.delta * this.timingFunc(progress)

      this.callback(currentValue)

      this.loop = requestAnimationFrame(this.animate)
    }
  }

  start = () => {
    this.onStart(this)
    this.loop = requestAnimationFrame((t) => {
      this.startTime = t
      this.animate(t)
    })
  }

  stop = () => {
    cancelAnimationFrame(this.loop)
  }
}

// Use the same instance of Ease for all window scroll
// easing to avoid clashes.
export const scrollEaser = new Ease()

// Smoothly scrolls the window to a y value.
export const smoothScroll = function (targetValue) {
  const initialValue = window.pageYOffset

  scrollEaser.stop()

  scrollEaser.set({
    callback: (y) => window.scrollTo(0, y),
    duration: 300,
    easing: 'easeInOutQuart',
    initialValue,
    targetValue,
  })

  scrollEaser.start()
}

// Smoothly scrolls to center an element within the viewport.
export const scrollToEl = function (el) {
  if (!el) {
    return
  }

  const winHeight = window.innerHeight
  const elCenter = el.offsetTop - (winHeight + el.offsetHeight) / 2 + 28 // add half header height
  const maxValue = document.body.scrollHeight - winHeight
  const targetValue = Math.min(elCenter, maxValue)

  if (targetValue > window.pageYOffset) {
    // Never reverse
    smoothScroll(targetValue)
  }
}

/**
 * Formats currencies for the current language and currency.
 * @param {number} num The number string to format.
 * @param {string} langCode The current language code.
 * @param {string} currency The current currency.
 */
export const formatPricing = (num, langCode = 'en-US', currency = 'USD') =>
  Number(num).toLocaleString(langCode, {
    style: 'currency',
    currency: currency || 'USD',
  })

export const handleCSV = (event, handleParse) => {
  if (event.target.files.length > 0 && window.FileReader) {
    const reader = new FileReader()

    reader.readAsText(event.target.files[0])
    reader.onload = (e) => {
      parse(e.target.result.trim(), {
        header: false,
        complete: (results) => handleParse(results.data),
      })
    }
  }
}

export function base64Encode(input) {
  if (!input) {
    return
  }

  let output = JSON.stringify(input)
  output = encodeURIComponent(output)
  output = unescape(output)
  return btoa(output)
}

export function base64Decode(input) {
  if (!input) {
    return
  }

  let output = atob(input)
  output = escape(output)
  output = decodeURIComponent(output)
  return JSON.parse(output)
}
